import React, { useState } from 'react';
import Header from '../common/Header';
import { getApiUrl } from '../../utils/apiConfig';
import { supabase } from '../../utils/supabase';
import { suggestDisplayUsernames } from '../../utils/usernameService';
import './StudentProfileForm.css';

const StudentProfileForm = () => {
  const [formData, setFormData] = useState({
    schoolName: '',
    currentGrade: '',
    teacherName: '',
    favoriteAnimal: '',
    favoriteRelax: '',
    favoriteSnack: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [funNames, setFunNames] = useState([]);
  const [displayUsername, setDisplayUsername] = useState('');
  const [funNameLoading, setFunNameLoading] = useState(false);
  const [funNameError, setFunNameError] = useState('');

  const canSuggestFunName = Boolean(
    formData.favoriteAnimal.trim() && formData.favoriteSnack.trim() && formData.favoriteRelax.trim()
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSuggestFunName = async () => {
    if (!canSuggestFunName || funNameLoading) return;
    setFunNameLoading(true);
    setFunNameError('');
    try {
      const suggestions = await suggestDisplayUsernames({
        animal: formData.favoriteAnimal,
        snack: formData.favoriteSnack,
        pastime: formData.favoriteRelax
      });
      setFunNames(suggestions);
      if (suggestions.length === 0) {
        setFunNameError('No fun names found this time. Try again!');
      }
    } catch (err) {
      setFunNameError(err.message);
    } finally {
      setFunNameLoading(false);
    }
  };

  const handleProfileSaveError = async (response) => {
    const errorData = await response.json();
    if (response.status === 409 && displayUsername) {
      // Someone else picked this fun name first; let the student choose again
      setDisplayUsername('');
      setFunNames(prev => prev.filter(name => name !== displayUsername));
    }
    throw new Error(errorData.error || 'Failed to save student profile');
  };

  const handleContinue = async () => {
    // Prevent duplicate submissions
    if (loading || submitted) {
      console.log('⚠️ Form submission already in progress or completed, ignoring duplicate click');
      return;
    }

    setLoading(true);
    setError('');
    
    // Simple validation
    if (
      !formData.schoolName ||
      !formData.currentGrade ||
      !formData.teacherName ||
      !formData.favoriteAnimal ||
      !formData.favoriteRelax ||
      !formData.favoriteSnack
    ) {
      setError('Please fill out all fields.');
      setLoading(false);
      return;
    }

    // The chosen fun name becomes the student's username
    if (!displayUsername) {
      setError('Please create and pick your fun name.');
      setLoading(false);
      return;
    }
    const username = displayUsername;

    try {
      // Get the user_id from Supabase session (user is logged in)
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.user?.id) {
        // Fall back to localStorage (for signup flow)
        const signupUserId = localStorage.getItem('signupUserId');
        if (!signupUserId) {
          throw new Error('No user ID found. Please log in or complete signup first.');
        }
        console.log('🔑 Using user_id from localStorage (signup flow):', signupUserId);
        
        // Continue with signupUserId
        const userId = signupUserId;
        
        // Send student profile data to backend
        console.log('🔄 Sending student profile data to backend...');
        const response = await fetch(getApiUrl('student-profile'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            schoolName: formData.schoolName,
            username: username,
            grade: formData.currentGrade,
            favoriteAnimal: formData.favoriteAnimal,
            relaxationMethod: formData.favoriteRelax,
            favoriteSnack: formData.favoriteSnack,
            Teacher: formData.teacherName,
            userId: userId,
            displayUsername: displayUsername
          })
        });

        if (!response.ok) {
          await handleProfileSaveError(response);
        }

        const result = await response.json();
        console.log('✅ Student profile saved to backend:', result);

        setSubmitted(true);
        localStorage.setItem('currentUserId', userId);
        console.log('💾 Stored user_id for BuddySelection:', userId);

        const profileData = {
          favoriteAnimal: formData.favoriteAnimal,
          favoriteSnack: formData.favoriteSnack,
          favoriteRelax: formData.favoriteRelax,
          grade: formData.currentGrade,
          schoolName: formData.schoolName,
          teacherName: formData.teacherName,
          username: username,
          displayUsername: displayUsername,
          name: localStorage.getItem('signupName') || ''
        };
        localStorage.setItem('studentProfileData', JSON.stringify(profileData));
        console.log('💾 Stored student profile data for FunFact:', profileData);

        localStorage.removeItem('signupUserId');
        localStorage.removeItem('signupUsername');
        console.log('🧹 Cleared signup-specific data from localStorage');

        console.log('🎯 Redirecting to BuddySelection page...');
        window.location.href = '/buddy-selection';
        return;
      }

      // User is logged in, use Supabase user ID
      const userId = session.user.id;
      const userMetadata = session.user.user_metadata || {};
      
      console.log('🔑 Using user_id from Supabase session:', userId);

      // Send student profile data to backend
      console.log('🔄 Sending student profile data to backend...');
      const response = await fetch(getApiUrl('student-profile'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          schoolName: formData.schoolName,
          username: username,
          grade: formData.currentGrade,
          favoriteAnimal: formData.favoriteAnimal,
          relaxationMethod: formData.favoriteRelax,
          favoriteSnack: formData.favoriteSnack,
          Teacher: formData.teacherName,
          userId: userId,
          displayUsername: displayUsername
        })
      });

      if (!response.ok) {
        await handleProfileSaveError(response);
      }

      const result = await response.json();
      console.log('✅ Student profile saved to backend:', result);

      setSubmitted(true);
      localStorage.setItem('currentUserId', userId);
      console.log('💾 Stored user_id for BuddySelection:', userId);

      const profileData = {
        favoriteAnimal: formData.favoriteAnimal,
        favoriteSnack: formData.favoriteSnack,
        favoriteRelax: formData.favoriteRelax,
        grade: formData.currentGrade,
        schoolName: formData.schoolName,
        teacherName: formData.teacherName,
        username: username,
        displayUsername: displayUsername,
        name: userMetadata.name || localStorage.getItem('signupName') || ''
      };
      localStorage.setItem('studentProfileData', JSON.stringify(profileData));
      console.log('💾 Stored student profile data for FunFact:', profileData);

      // Clear signup data if it exists
      localStorage.removeItem('signupUserId');
      localStorage.removeItem('signupUsername');
      console.log('🧹 Cleared signup-specific data from localStorage');

      console.log('🎯 Redirecting to BuddySelection page...');
      window.location.href = '/buddy-selection';
      
    } catch (error) {
      console.error('Error saving student profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="student-profile-page">
      <Header />
      <div className="student-profile-main">
        <div className="student-profile-header">
          <p className="student-profile-title">
            Welcome <strong>Student!</strong> <br />
            Please introduce yourself in this profile form below.
          </p>
        </div>

        <form className="student-profile-form" onSubmit={e => e.preventDefault()}>
          {/* Left Column */}
          <div>
            {error && (
              <div className="student-profile-error">{error}</div>
            )}
            <div className="student-profile-form-group">
              <label className="student-profile-label">School Name</label>
              <input
                type="text"
                name="schoolName"
                value={formData.schoolName}
                onChange={handleInputChange}
                required
                className="student-profile-input"
              />
            </div>
            <div className="student-profile-form-group">
              <label className="student-profile-label">Current Grade</label>
              <input
                type="text"
                name="currentGrade"
                value={formData.currentGrade}
                onChange={handleInputChange}
                required
                className="student-profile-input"
              />
            </div>
            <div className="student-profile-form-group">
              <label className="student-profile-label">Homeroom Teacher or Counselor</label>
              <input
                type="text"
                name="teacherName"
                value={formData.teacherName}
                onChange={handleInputChange}
                required
                className="student-profile-input"
              />
            </div>
          </div>

          {/* Right Column */}
          <div>
            <div className="student-profile-form-group">
              <label className="student-profile-label">What is your favorite animal?</label>
              <input
                type="text"
                name="favoriteAnimal"
                value={formData.favoriteAnimal}
                onChange={handleInputChange}
                required
                className="student-profile-input"
              />
            </div>
            <div className="student-profile-form-group">
              <label className="student-profile-label">What is your favorite thing to do to relax?</label>
              <input
                type="text"
                name="favoriteRelax"
                value={formData.favoriteRelax}
                onChange={handleInputChange}
                required
                className="student-profile-input"
              />
            </div>
            <div className="student-profile-form-group">
              <label className="student-profile-label">What is your favorite snack?</label>
              <input
                type="text"
                name="favoriteSnack"
                value={formData.favoriteSnack}
                onChange={handleInputChange}
                required
                className="student-profile-input"
              />
            </div>
          </div>
        </form>

        <div className="student-profile-funname">
          <p className="student-profile-label">Your fun name (this will be your username)</p>
          <p className="student-profile-funname-hint">
            {canSuggestFunName
              ? 'We can mix your favorite animal, snack and relaxing activity into a one-of-a-kind name!'
              : 'Fill in your favorite animal, relaxing activity and snack to create a fun name.'}
          </p>
          <button
            type="button"
            onClick={handleSuggestFunName}
            disabled={!canSuggestFunName || funNameLoading || submitted}
            className="student-profile-funname-btn app-btn"
          >
            {funNameLoading ? 'Thinking...' : funNames.length > 0 ? '↻ Try new names' : '✨ Create my fun name'}
          </button>
          {funNameError && (
            <div className="student-profile-funname-error">{funNameError}</div>
          )}
          {funNames.length > 0 && (
            <div className="student-profile-funname-chips">
              {funNames.map(name => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setDisplayUsername(name)}
                  disabled={submitted}
                  className={`student-profile-funname-chip${displayUsername === name ? ' selected' : ''}`}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
          {displayUsername && (
            <p className="student-profile-funname-selected">
              Selected: <strong>{displayUsername}</strong>
              <button
                type="button"
                onClick={() => setDisplayUsername('')}
                disabled={submitted}
                className="student-profile-funname-clear"
                aria-label="Clear fun name"
              >
                ✕
              </button>
            </p>
          )}
        </div>

        <div className="student-profile-footer">
          <button
            type="button"
            onClick={handleContinue}
            disabled={loading || submitted}
            className="student-profile-continue-btn app-btn"
          >
            {submitted ? 'Profile Submitted!' : loading ? 'Continuing...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentProfileForm;